import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { INLINE, htmlToMarkdown, safeUrl } from "../readme-text.js";
import css from './Markdown.module.css';
/**
 * Split a `[label](target "title")` tail into its parts.
 * @param body - the text inside the parentheses.
 * @returns the target URL with any title stripped.
 */
function linkTarget(body) {
    const space = body.indexOf(' ');
    return space === -1 ? body : body.slice(0, space);
}
/**
 * Render inline Markdown into React nodes.
 * @param text - one block's text.
 * @param baseUrl - the document's location, for relative links.
 * @param keyPrefix - key namespace, so sibling blocks cannot collide.
 * @returns the rendered children.
 */
function renderInline(text, baseUrl, keyPrefix) {
    const out = [];
    let rest = text;
    let index = 0;
    while (rest.length > 0) {
        const match = INLINE.exec(rest);
        if (match === null || match.index === undefined) {
            out.push(rest);
            break;
        }
        if (match.index > 0)
            out.push(rest.slice(0, match.index));
        const token = match[0];
        const key = `${keyPrefix}-${index}`;
        index += 1;
        if (token.startsWith('`')) {
            out.push(_jsx("code", { className: css.code, children: token.slice(1, -1) }, key));
        }
        else if (token.startsWith('[![')) {
            // A badge: an image wrapped in a link. Both URLs are vetted, and the
            // alt text stands in whenever the image URL is not usable.
            const inner = token.slice(1, token.lastIndexOf(']('));
            const label = inner.slice(2, inner.indexOf(']'));
            const image = safeUrl(linkTarget(inner.slice(inner.indexOf('](') + 2, -1)), baseUrl);
            const href = safeUrl(linkTarget(token.slice(token.lastIndexOf('](') + 2, -1)), baseUrl);
            const content = image === undefined
                ? label
                : _jsx("img", { className: css.badge, src: image, alt: label, loading: "lazy" });
            out.push(href === undefined
                ? _jsx("span", { children: content }, key)
                : _jsx("a", { className: css.badgeLink, href: href, target: "_blank", rel: "noreferrer noopener", children: content }, key));
        }
        else if (token.startsWith('![')) {
            const label = token.slice(2, token.indexOf(']'));
            const href = safeUrl(linkTarget(token.slice(token.indexOf('](') + 2, -1)), baseUrl);
            // A README's images are mostly status badges. They are decorative and
            // often slow, so alt text stands in when the URL is unusable.
            out.push(href === undefined
                ? _jsx("span", { children: label }, key)
                : _jsx("img", { className: css.image, src: href, alt: label, loading: "lazy" }, key));
        }
        else if (token.startsWith('[')) {
            const label = token.slice(1, token.indexOf(']'));
            const href = safeUrl(linkTarget(token.slice(token.indexOf('](') + 2, -1)), baseUrl);
            out.push(href === undefined
                ? _jsx("span", { children: label }, key)
                : _jsx("a", { className: css.link, href: href, target: "_blank", rel: "noreferrer noopener", children: label }, key));
        }
        else if (token.startsWith('<')) {
            const href = safeUrl(token.slice(1, -1), baseUrl);
            out.push(href === undefined
                ? _jsx("span", { children: token }, key)
                : _jsx("a", { className: css.link, href: href, target: "_blank", rel: "noreferrer noopener", children: href }, key));
        }
        else if (token.startsWith('**') || token.startsWith('__')) {
            out.push(_jsx("strong", { children: token.slice(2, -2) }, key));
        }
        else if (token.startsWith('~~')) {
            out.push(_jsx("del", { children: token.slice(2, -2) }, key));
        }
        else {
            out.push(_jsx("em", { children: token.slice(1, -1) }, key));
        }
        rest = rest.slice(match.index + token.length);
    }
    return out;
}
/**
 * Strip the pipes and padding from one table row.
 * @param line - the raw row.
 * @returns the trimmed cells.
 */
function tableCells(line) {
    return line.replace(/^\s*\|/, '').replace(/\|\s*$/, '').split('|').map(cell => cell.trim());
}
/**
 * Render Markdown as React elements.
 *
 * Parsing is a single forward pass over lines: each iteration either consumes
 * a multi-line construct wholesale (fence, list, quote, table) or emits one
 * block. There is no backtracking, so malformed input ends the block it is in
 * rather than desynchronising the rest of the document.
 * @param props - the source and its base URL.
 * @returns the rendered document.
 */
export function Markdown(props) {
    const { source, baseUrl } = props;
    const lines = htmlToMarkdown(source.replace(/\r\n?/g, '\n')).split('\n');
    const blocks = [];
    let cursor = 0;
    let key = 0;
    /** Consume lines while they satisfy a predicate. */
    const takeWhile = (predicate) => {
        const taken = [];
        while (cursor < lines.length && predicate(lines[cursor])) {
            taken.push(lines[cursor]);
            cursor += 1;
        }
        return taken;
    };
    while (cursor < lines.length) {
        const line = lines[cursor];
        const id = `b${key}`;
        key += 1;
        if (line.trim() === '') {
            cursor += 1;
            key -= 1;
            continue;
        }
        // Fenced code. An unterminated fence runs to the end of the document,
        // which is what every Markdown implementation does.
        const fence = /^\s*(```+|~~~+)(.*)$/.exec(line);
        if (fence !== null) {
            const marker = fence[1].slice(0, 3);
            cursor += 1;
            const body = takeWhile(current => !current.trimStart().startsWith(marker));
            if (cursor < lines.length)
                cursor += 1;
            blocks.push(_jsx("pre", { className: css.pre, "data-lang": (fence[2] ?? '').trim() || undefined, children: _jsx("code", { children: body.join('\n') }) }, id));
            continue;
        }
        const heading = /^(#{1,6})\s+(.*)$/.exec(line);
        if (heading !== null) {
            const level = heading[1].length;
            const Tag = `h${Math.min(level + 1, 6)}`;
            cursor += 1;
            blocks.push(_jsx(Tag, { className: css.heading, "data-level": level, children: renderInline(heading[2].replace(/\s*#+\s*$/, ''), baseUrl, id) }, id));
            continue;
        }
        // Three or more of the same marker, optionally spaced: a thematic break.
        if (/^\s*(?:-\s*){3,}$|^\s*(?:\*\s*){3,}$|^\s*(?:_\s*){3,}$/.test(line)) {
            cursor += 1;
            blocks.push(_jsx("hr", { className: css.rule }, id));
            continue;
        }
        // Tables need a delimiter row directly under the header to be a table at
        // all; without one the pipes are just text.
        const next = lines[cursor + 1];
        if (line.includes('|') && next !== undefined && /^\s*\|?[\s:|-]+\|[\s:|-]*$/.test(next)) {
            const header = tableCells(line);
            cursor += 2;
            const rows = takeWhile(current => current.includes('|') && current.trim() !== '').map(tableCells);
            blocks.push(_jsx("div", { className: css.tableWrap, children: _jsxs("table", { className: css.table, children: [_jsx("thead", { children: _jsx("tr", { children: header.map((cell, i) => _jsx("th", { children: renderInline(cell, baseUrl, `${id}h${i}`) }, i)) }) }), _jsx("tbody", { children: rows.map((row, r) => (_jsx("tr", { children: row.map((cell, c) => _jsx("td", { children: renderInline(cell, baseUrl, `${id}${r}c${c}`) }, c)) }, r))) })] }) }, id));
            continue;
        }
        if (/^\s*>/.test(line)) {
            const body = takeWhile(current => /^\s*>/.test(current) || current.trim() !== '');
            blocks.push(_jsx("blockquote", { className: css.quote, children: renderInline(body.map(current => current.replace(/^\s*>\s?/, '')).join(' '), baseUrl, id) }, id));
            continue;
        }
        const bullet = /^\s*([-*+]|\d+[.)])\s+/;
        if (bullet.test(line)) {
            const ordered = /^\s*\d/.test(line);
            const items = [];
            while (cursor < lines.length) {
                const current = lines[cursor];
                if (bullet.test(current)) {
                    items.push(current.replace(bullet, ''));
                    cursor += 1;
                }
                else if (current.trim() !== '' && items.length > 0 && /^\s{2,}/.test(current)) {
                    // A wrapped continuation line belongs to the item above it.
                    items[items.length - 1] += ` ${current.trim()}`;
                    cursor += 1;
                }
                else
                    break;
            }
            const children = items.map((item, i) => (_jsx("li", { children: renderInline(item, baseUrl, `${id}i${i}`) }, i)));
            blocks.push(ordered
                ? _jsx("ol", { className: css.list, children: children }, id)
                : _jsx("ul", { className: css.list, children: children }, id));
            continue;
        }
        const paragraph = takeWhile(current => current.trim() !== ''
            && !bullet.test(current)
            && !/^\s*>/.test(current)
            && !/^(#{1,6})\s/.test(current)
            && !/^\s*(```|~~~)/.test(current));
        blocks.push(_jsx("p", { className: css.paragraph, children: renderInline(paragraph.join(' '), baseUrl, id) }, id));
    }
    return _jsx("div", { className: css.root, children: blocks });
}
